import {useMemo} from 'react';
import type {FinancialYear} from '../types/vault';
import {formatAUD} from '../utils/clipboard';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

interface TaxTrendProps {
  profileName: string;
  years: FinancialYear[];
  showSummaryCards?: boolean;
}

// ── Internal: compact label for chart data points ──
function pointLabelAUD(val: number): string {
  if (val === 0) return '$0';
  if (val >= 1000000) {
    const m = val / 1000000;
    return `$${m.toFixed(1).replace('.0', '')}M`;
  }
  if (val >= 100000) return `$${Math.round(val / 1000)}k`;
  if (val >= 1000) {
    const k = val / 1000;
    return `$${k.toFixed(1).replace('.0', '')}k`;
  }
  return `$${val}`;
}

// ── Internal: nice axis maximum with headroom ──
function computeAxis(dataMax: number): {max: number;gridlines: number[]} {
  if (dataMax <= 0) return {max: 100000,gridlines: [0,25000,50000,75000,100000]};
  const withHeadroom = dataMax * 1.18;
  const rawStep = withHeadroom / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  let niceStep: number;
  if (norm <= 1) niceStep = mag;
  else if (norm <= 2) niceStep = 2 * mag;
  else if (norm <= 2.5) niceStep = 2.5 * mag;
  else if (norm <= 5) niceStep = 5 * mag;
  else niceStep = 10 * mag;
  const max = Math.ceil(withHeadroom / niceStep) * niceStep;
  const gridlines: number[] = [];
  for (let v = 0; v <= max; v += niceStep) gridlines.push(v);
  return {max, gridlines};
}

// ── Chart layout constants ──
const CL = 58, CR = 12, CT = 22, CB = 36;
const SW = 800, SH = 250;
const PW = SW - CL - CR, PH = SH - CT - CB;

const {FiCalendar, FiBarChart2, FiTrendingUp, FiTrendingDown, FiMinus, FiDollarSign, FiCheckCircle, FiAlertTriangle} = FiIcons;

export function TaxTrendSection({profileName, years, showSummaryCards = false}: TaxTrendProps) {
  const sorted = useMemo(() =>
    [...years].filter(y => !y.archived).sort((a, b) => a.endingYear - b.endingYear)
  , [years]);

  const {max: axisMax, gridlines} = useMemo(() => {
    let dm = 0;
    sorted.forEach(y => {
      if ((y.grossIncome ?? 0) > dm) dm = y.grossIncome!;
      if ((y.taxableIncome ?? 0) > dm) dm = y.taxableIncome!;
      if ((y.taxWithheld ?? 0) > dm) dm = y.taxWithheld!;
    });
    return computeAxis(dm);
  }, [sorted]);

  if (sorted.length < 2) {
    return (
      <section className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 text-center mt-6">
        <h2 className="text-base font-medium text-slate-900 mb-1">Tax Position Over Time</h2>
        <p className="text-sm text-slate-500">Add another financial year to view trends over time.</p>
      </section>
    );
  }

  // ── Cumulative totals ──
  const totalGrossIncome = sorted.reduce((s, y) => y.grossIncome != null ? s + y.grossIncome : s, 0);
  const totalTaxableIncome = sorted.reduce((s, y) => y.taxableIncome != null ? s + y.taxableIncome : s, 0);
  const totalTaxWithheld = sorted.reduce((s, y) => y.taxWithheld != null ? s + y.taxWithheld : s, 0);
  const totalRefunds = sorted.reduce((s, y) => (y.taxRefund ?? 0) > 0 ? s + y.taxRefund! : s, 0);
  const totalPayables = sorted.reduce((s, y) => (y.taxPayable ?? 0) > 0 ? s + y.taxPayable! : s, 0);

  // ── Chart coordinate helpers ──
  const n = sorted.length;
  const xP = (i: number) => CL + (i / (n - 1)) * PW;
  const yP = (v: number) => CT + PH - (v / axisMax) * PH;

  const buildPath = (key: keyof FinancialYear) => {
    let d = '', first = true;
    sorted.forEach((y, i) => {
      const v = y[key] as number | null | undefined;
      if (v == null) { first = true; return; }
      d += first ? `M${xP(i)},${yP(v)} ` : `L${xP(i)},${yP(v)} `;
      first = false;
    });
    return d;
  };

  // ── Labeled series renderer ──
  const renderLabeledSeries = (key: keyof FinancialYear, color: string, pos: 'above' | 'auto') => {
    const pts: {x: number;y: number;val: number;idx: number}[] = [];
    sorted.forEach((y, i) => {
      const v = y[key] as number | null | undefined;
      if (v != null) pts.push({x: xP(i), y: yP(v), val: v, idx: i});
    });
    return pts.map(p => {
      const off = 17;
      let ly: number;
      if (pos === 'auto') {
        const gv = sorted[p.idx].grossIncome;
        if (gv != null && Math.abs(yP(gv) - p.y) < 32) {
          ly = p.y + off + 6;
        } else {
          ly = p.y - off;
        }
      } else {
        ly = p.y - off;
      }
      ly = Math.max(10, Math.min(SH - 6, ly));
      const lx = Math.max(CL + 28, Math.min(SW - CR - 28, p.x));
      const txt = pointLabelAUD(p.val);
      const rw = txt.length * 5.2 + 12, rh = 14;
      return (
        <g key={`${sorted[p.idx].id}-${key}`}>
          <circle cx={p.x} cy={p.y} r="3.5" fill={color} stroke="#fff" strokeWidth="2" />
          <rect x={lx - rw / 2} y={ly - rh / 2} width={rw} height={rh} rx={3}
            fill="white" fillOpacity={0.93} stroke="#e2e8f0" strokeWidth={0.5} />
          <text x={lx} y={ly + 3.5} textAnchor="middle" fontSize={8.5}
            fontWeight={600} fill={color} fontFamily="system-ui,sans-serif">{txt}</text>
        </g>
      );
    });
  };

  // ── KPI helpers ──
  const latest = sorted[n - 1], prev = sorted[n - 2];
  const latestTaxable = latest.taxableIncome;
  const latestTaxableDisp = latestTaxable != null ? formatAUD(latestTaxable) : 'Not recorded';
  const prevTaxable = prev.taxableIncome;
  let diffDisp = 'Not enough data', diffIcon: any = null, diffColor = 'text-slate-500';
  if (latestTaxable != null && prevTaxable != null) {
    const diff = latestTaxable - prevTaxable;
    const abs = formatAUD(Math.abs(diff));
    let pct = '';
    if (prevTaxable !== 0) {
      const p = (diff / Math.abs(prevTaxable)) * 100;
      pct = ` · ${diff > 0 ? '+' : diff < 0 ? '-' : ''}${Math.abs(p).toFixed(1)}%`;
    }
    if (diff > 0) { diffDisp = `+${abs}${pct}`; diffIcon = FiTrendingUp; diffColor = 'text-green-600'; }
    else if (diff < 0) { diffDisp = `-${abs}${pct}`; diffIcon = FiTrendingDown; diffColor = 'text-amber-600'; }
    else { diffDisp = `${abs}${prevTaxable !== 0 ? ' · 0.0%' : ''}`; diffIcon = FiMinus; diffColor = 'text-slate-500'; }
  }
  const latestWithheld = latest.taxWithheld;
  const latestWithheldDisp = latestWithheld != null ? formatAUD(latestWithheld) : 'Not recorded';
  let outcomeDisp = 'No Balance', outcomeColor = 'text-slate-500';
  let outcomeIcon: any = FiMinus, outcomeTile = 'bg-slate-50', outcomeIconColor = 'text-slate-500';
  if ((latest.taxRefund ?? 0) > 0) {
    outcomeDisp = `Refund ${formatAUD(latest.taxRefund)}`; outcomeColor = 'text-green-600';
    outcomeIcon = FiCheckCircle; outcomeTile = 'bg-green-50'; outcomeIconColor = 'text-green-600';
  } else if ((latest.taxPayable ?? 0) > 0) {
    outcomeDisp = `Payable ${formatAUD(latest.taxPayable)}`; outcomeColor = 'text-amber-600';
    outcomeIcon = FiAlertTriangle; outcomeTile = 'bg-amber-50'; outcomeIconColor = 'text-amber-600';
  }

  // ── Change card dynamic styling ──
  let changeTile = 'bg-slate-50', changeIconColor = 'text-slate-500';
  if (diffIcon === FiTrendingUp) { changeTile = 'bg-green-50'; changeIconColor = 'text-green-600'; }
  else if (diffIcon === FiTrendingDown) { changeTile = 'bg-amber-50'; changeIconColor = 'text-amber-600'; }

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden mt-6">
      {/* ── Header ── */}
      <div className="px-5 py-3 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Tax Position Over Time</h2>
          <p className="text-xs text-slate-500">{profileName} · FY{sorted[0].endingYear}–FY{sorted[n - 1].endingYear}</p>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5" />
          <span>{n} Financial Years</span>
        </div>
      </div>

      {/* ── KPI Cards + Cumulative Totals ── */}
      {showSummaryCards && (
        <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50/40 space-y-3.5">
          {/* Latest-year KPI row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2.5">
            {/* Latest Taxable Income */}
            <div className="bg-white rounded-xl p-3 border border-slate-100 shadow-sm flex items-start gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                <SafeIcon icon={FiBarChart2} className="w-4.5 h-4.5 text-teal-700" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] text-slate-500 leading-tight">Latest Taxable Income</p>
                <p className="text-base font-semibold text-teal-700 leading-snug">{latestTaxableDisp}</p>
                <p className="text-[10px] text-slate-400">FY{latest.endingYear}</p>
              </div>
            </div>
            {/* Taxable Income Change */}
            <div className="bg-white rounded-xl p-3 border border-slate-100 shadow-sm flex items-start gap-2.5">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${changeTile}`}>
                <SafeIcon icon={diffIcon || FiMinus} className={`w-4.5 h-4.5 ${changeIconColor}`} />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] text-slate-500 leading-tight">Taxable Income Change</p>
                {diffDisp !== 'Not enough data' ? (
                  <p className={`text-base font-semibold leading-snug ${diffColor}`}>{diffDisp}</p>
                ) : (
                  <p className="text-base font-semibold text-slate-900 leading-snug">{diffDisp}</p>
                )}
                <p className="text-[10px] text-slate-400">vs FY{prev.endingYear}</p>
              </div>
            </div>
            {/* Latest Tax Withheld */}
            <div className="bg-white rounded-xl p-3 border border-slate-100 shadow-sm flex items-start gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                <SafeIcon icon={FiDollarSign} className="w-4.5 h-4.5 text-amber-700" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] text-slate-500 leading-tight">Latest Tax Withheld</p>
                <p className="text-base font-semibold text-slate-900 leading-snug">{latestWithheldDisp}</p>
                <p className="text-[10px] text-slate-400">FY{latest.endingYear}</p>
              </div>
            </div>
            {/* Latest Outcome */}
            <div className="bg-white rounded-xl p-3 border border-slate-100 shadow-sm flex items-start gap-2.5">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${outcomeTile}`}>
                <SafeIcon icon={outcomeIcon} className={`w-4.5 h-4.5 ${outcomeIconColor}`} />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] text-slate-500 leading-tight">Latest Outcome</p>
                <p className={`text-base font-semibold leading-snug ${outcomeColor}`}>{outcomeDisp}</p>
                <p className="text-[10px] text-slate-400">FY{latest.endingYear}</p>
              </div>
            </div>
          </div>

          {/* Cumulative Totals panel */}
          <div className="bg-[#102D4C]/[0.03] rounded-xl p-3.5 border border-slate-100">
            <div className="flex items-center justify-between mb-2.5">
              <h3 className="text-xs font-semibold text-slate-800">Cumulative Totals</h3>
              <p className="text-[10px] text-slate-500">Across {n} Financial Years</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2.5">
              {/* Total Gross Income */}
              <div className="bg-white rounded-lg p-2.5 border border-slate-100 shadow-sm flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-md bg-emerald-50 flex items-center justify-center shrink-0">
                  <SafeIcon icon={FiTrendingUp} className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-slate-500 leading-tight">Total Gross Income</p>
                  <p className="text-sm font-semibold text-slate-900 leading-snug">{formatAUD(totalGrossIncome)}</p>
                </div>
              </div>
              {/* Total Taxable Income */}
              <div className="bg-white rounded-lg p-2.5 border border-slate-100 shadow-sm flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-md bg-teal-50 flex items-center justify-center shrink-0">
                  <SafeIcon icon={FiBarChart2} className="w-4 h-4 text-teal-700" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-slate-500 leading-tight">Total Taxable Income</p>
                  <p className="text-sm font-semibold text-slate-900 leading-snug">{formatAUD(totalTaxableIncome)}</p>
                </div>
              </div>
              {/* Total Tax Withheld */}
              <div className="bg-white rounded-lg p-2.5 border border-slate-100 shadow-sm flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-md bg-amber-50 flex items-center justify-center shrink-0">
                  <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-amber-700" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-slate-500 leading-tight">Total Tax Withheld</p>
                  <p className="text-sm font-semibold text-slate-900 leading-snug">{formatAUD(totalTaxWithheld)}</p>
                </div>
              </div>
              {/* Refunds | Payables */}
              <div className="bg-white rounded-lg p-2.5 border border-slate-100 shadow-sm flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-md bg-slate-50 flex items-center justify-center shrink-0">
                  <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-slate-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center divide-x divide-slate-200">
                    <div className="pr-2.5 flex-1 min-w-0">
                      <p className="text-[10px] text-slate-500 leading-tight">Refunds</p>
                      <p className="text-sm font-semibold text-green-600 leading-snug">{formatAUD(totalRefunds)}</p>
                    </div>
                    <div className="pl-2.5 flex-1 min-w-0">
                      <p className="text-[10px] text-slate-500 leading-tight">Payables</p>
                      <p className="text-sm font-semibold text-amber-600 leading-snug">{formatAUD(totalPayables)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Chart ── */}
      <div className="overflow-x-auto">
        <div style={{minWidth: `${Math.max(580, n * 110)}px`}} className="px-4 pt-3 pb-1">
          {/* Legend */}
          <div className="flex items-center justify-center gap-5 mb-2" aria-hidden="true">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#102D4C]" /><span className="text-[11px] text-slate-600">Gross Income</span></span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#0F766E]" /><span className="text-[11px] text-slate-600">Taxable Income</span></span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#B98937]" /><span className="text-[11px] text-slate-600">Tax Withheld</span></span>
          </div>
          <svg viewBox={`0 0 ${SW} ${SH}`} className="w-full h-auto" role="img" aria-label={`Tax trend chart for ${profileName}`}>
            <title>Tax Position Over Time</title>
            {/* Gridlines */}
            {gridlines.map(v => {
              const gy = yP(v);
              return <line key={v} x1={CL} y1={gy} x2={SW - CR} y2={gy}
                stroke={v === 0 ? '#e2e8f0' : '#f1f5f9'} strokeWidth={v === 0 ? 1.5 : 1} />;
            })}
            {/* Y-axis labels */}
            {gridlines.map(v => (
              <text key={`yl-${v}`} x={CL - 6} y={yP(v) + 3} textAnchor="end"
                fontSize={8.5} fill="#94a3b8" fontFamily="system-ui,sans-serif">{pointLabelAUD(v)}</text>
            ))}
            {/* X-axis labels */}
            {sorted.map((y, i) => (
              <text key={y.id} x={xP(i)} y={SH - 8} textAnchor="middle"
                fontSize={9} fill="#64748b" fontFamily="system-ui,sans-serif" fontWeight={500}>FY{y.endingYear}</text>
            ))}
            {/* Series paths */}
            <path d={buildPath('grossIncome')} fill="none" stroke="#102D4C" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
            <path d={buildPath('taxableIncome')} fill="none" stroke="#0F766E" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
            <path d={buildPath('taxWithheld')} fill="none" stroke="#B98937" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
            {/* Labeled points */}
            {renderLabeledSeries('grossIncome', '#102D4C', 'above')}
            {renderLabeledSeries('taxableIncome', '#0F766E', 'auto')}
            {renderLabeledSeries('taxWithheld', '#B98937', 'above')}
          </svg>
        </div>
      </div>

      {/* ── Annual Outcome Cards ── */}
      <div className="flex gap-2 overflow-x-auto px-4 py-3 border-t border-slate-100 bg-slate-50/50">
        {sorted.map((y, i) => {
          const isLatest = i === n - 1;
          let label = 'No Balance', amount = '', color = 'text-slate-400', bg = 'bg-slate-50', icon: any = FiMinus, ic = 'text-slate-400';
          if ((y.taxRefund ?? 0) > 0) {
            label = 'Refund'; amount = formatAUD(y.taxRefund); color = 'text-green-600';
            bg = 'bg-green-50'; icon = FiCheckCircle; ic = 'text-green-500';
          } else if ((y.taxPayable ?? 0) > 0) {
            label = 'Payable'; amount = formatAUD(y.taxPayable); color = 'text-amber-600';
            bg = 'bg-amber-50'; icon = FiAlertTriangle; ic = 'text-amber-500';
          }
          return (
            <div key={y.id} className={`flex-shrink-0 rounded-lg border p-2.5 min-w-[110px] text-center transition-colors ${
              isLatest ? 'border-teal-200 bg-teal-50/20' : 'border-slate-100 bg-white'}`}>
              <p className="text-[10px] font-medium text-slate-500 mb-1">FY{y.endingYear}{isLatest ? ' ★' : ''}</p>
              <div className={`w-6 h-6 rounded-full ${bg} flex items-center justify-center mx-auto mb-1`}>
                <SafeIcon icon={icon} className={`w-3.5 h-3.5 ${ic}`} />
              </div>
              <p className={`text-xs font-semibold ${color}`}>{label}</p>
              {amount && <p className={`text-[11px] font-medium ${color} mt-0.5`}>{amount}</p>}
            </div>
          );
        })}
      </div>

      {/* ── Footnote ── */}
      <p className="px-5 py-2 text-[10px] text-slate-400 border-t border-slate-100">
        All values are in AUD. Amounts are rounded for chart display only.
      </p>
    </section>
  );
}